import { Component } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import Notification from "components/DigiHapi/Notification";

import { FaRegNewspaper } from "react-icons/fa";
import { RiHeartsFill } from "react-icons/ri";

import {
	fetchGetTemplatesCategories,
	fetchReorderTemplatesCategories
} from "actions/template";
import { TEMPLATE_ON_DRAG_END } from "actions/types";
import { fetchGetPictosUri } from "actions/index";

// DigiHapi palette used by the drag & drop zones
const colors = {
	brown: "#ba986b",
	lightBrown: "#cab08e",
	beige: "#eae0d2",
	darkBeige: "#e2d4c1",
	lightInfo: "#d9edf7",
	darkInfo: "#c4e3f3",
	lightSuccess: "#dff0d8",
	darkSuccess: "#66bb6a",
	blue: "#49accd"
};

// Horizontal list of draggable templates
const templatesZoneSx = backgroundColor => ({
	display: "flex",
	mt: 1,
	border: "1px solid lightgrey",
	borderRadius: "3px",
	minHeight: "90px",
	overflow: "auto",
	p: "5px",
	backgroundColor
});

class Template extends Component {
	render() {
		const { template, index, pictos } = this.props;
		const src = template.photoUri
			? template.photoUri
			: pictos && template.picto
			? pictos[template.picto]
			: null;
		return (
			<Draggable draggableId={template._id} index={index} type="TEMPLATE">
				{(provided, snapshot) => (
					<MDBox
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						ref={provided.innerRef}
						display="flex"
						flexDirection="column"
						alignItems="center"
						flexShrink={0}
						width="85px"
						height="85px"
						m="5px"
						pt="5px"
						borderRadius="sm"
						sx={{
							backgroundColor: snapshot.isDragging ? colors.blue : "white",
							border: `1px solid ${colors.brown}`
						}}
					>
						<MDBox width="50px" height="50px">
							{src && (
								<MDBox
									component="img"
									src={src}
									alt={
										template.photoUri
											? template.name
											: `${template.picto} made by Freepik from www.flaticon.com`
									}
									width="100%"
									height="100%"
									sx={{ objectFit: "contain" }}
								/>
							)}
						</MDBox>
						<MDTypography
							variant="caption"
							display="block"
							width="85px"
							textAlign="center"
							noWrap
							px={0.5}
						>
							{template.name}
						</MDTypography>
					</MDBox>
				)}
			</Draggable>
		);
	}
}

class TemplateCategory extends Component {
	render() {
		const { category, index, templates } = this.props;
		return (
			<Draggable draggableId={category._id} index={index} type="CATEGORY">
				{(provided, snapshot) => (
					<MDBox
						{...provided.draggableProps}
						ref={provided.innerRef}
						mt={1}
						p="5px"
						borderRadius="sm"
						sx={{
							border: "1px solid lightgrey",
							backgroundColor: snapshot.isDragging
								? colors.darkSuccess
								: colors.lightSuccess
						}}
					>
						<MDTypography variant="h6" {...provided.dragHandleProps}>
							{category.name}
						</MDTypography>
						<Droppable
							droppableId={category._id}
							direction="horizontal"
							type="TEMPLATE"
						>
							{(provided, snapshot) => (
								<MDBox
									ref={provided.innerRef}
									{...provided.droppableProps}
									sx={templatesZoneSx(
										snapshot.isDraggingOver ? colors.darkInfo : colors.lightInfo
									)}
								>
									{category.templateIds.map((templateId, index) => (
										<ConnectedTemplate
											key={templates[templateId]._id}
											template={templates[templateId]}
											index={index}
										/>
									))}
									{provided.placeholder}
								</MDBox>
							)}
						</Droppable>
					</MDBox>
				)}
			</Draggable>
		);
	}
}

class TemplatesOrder extends Component {
	constructor(props) {
		super(props);
		this.state = {
			br: false,
			color: "info"
		};
	}

	componentDidMount() {
		const {
			fetchGetPictosUri,
			fetchGetTemplatesCategories,
			type,
			system = false,
			community,
			pictos
		} = this.props;
		if (!pictos) {
			fetchGetPictosUri();
		}
		fetchGetTemplatesCategories(type, system, community._id);
	}

	componentDidUpdate(prevProps, prevState) {
		const {
			fetchGetTemplatesCategories,
			type,
			system = false,
			snackInfoMsg,
			snackErrorMsg,
			community
		} = this.props;

		if (snackErrorMsg && prevProps.snackErrorMsg !== snackErrorMsg) {
			this.autoCloseMessage();
		}

		if (snackInfoMsg && prevProps.snackInfoMsg !== snackInfoMsg) {
			fetchGetTemplatesCategories(type, system, community._id);
			this.autoCloseMessage();
		}
	}

	hideAlert = () => {
		this.setState({
			br: false
		});
	};

	autoCloseMessage = () => {
		const { snackErrorMsg, snackInfoMsg } = this.props;

		this.setState({
			br: true,
			message: snackInfoMsg ? snackInfoMsg : snackErrorMsg,
			color: snackInfoMsg ? "info" : "danger"
		});

		if (snackInfoMsg) {
			setTimeout(this.hideAlert, 5000);
		}
	};

	onDragEnd = result => {
		this.props.dispatch({
			type: TEMPLATE_ON_DRAG_END,
			result
		});
	};

	render() {
		const {
			categories,
			fetchReorderTemplatesCategories,
			fetchGetTemplatesCategories,
			areCategoriesReordered,
			type,
			templates,
			system = false,
			community
		} = this.props;
		const { color, message, br } = this.state;

		let category;
		let others;
		if (categories && Object.values(categories).length > 0) {
			[category, ...others] = Object.values(categories);
		}
		return (
			<Grid container spacing={3} justifyContent="center">
				<Grid item xs={12}>
					<Card>
						<MDBox
							mx={2}
							mt={-3}
							py={2}
							px={2}
							variant="gradient"
							bgColor="primary"
							borderRadius="lg"
							coloredShadow="primary"
							display="flex"
							alignItems="center"
							color="white"
						>
							{type === "Activity" ? (
								<RiHeartsFill size={24} />
							) : (
								<FaRegNewspaper size={24} />
							)}
							<MDTypography variant="h6" color="white" ml={1.5}>
								{"Réorganiser "}
								{type === "Activity" ? "les instants partage" : "les modèles"}
							</MDTypography>
						</MDBox>
						<MDBox p={2}>
							{categories && Object.values(categories).length > 0 && (
								<DragDropContext onDragEnd={this.onDragEnd}>
									<MDBox
										mt={1}
										p="5px"
										borderRadius="sm"
										sx={{
											border: "1px solid lightgrey",
											backgroundColor: colors.lightBrown
										}}
									>
										<MDTypography variant="h6">
											{"Piochez ou retirez des éléments ici"}
										</MDTypography>
										<Droppable
											droppableId={category._id}
											direction="horizontal"
											type="TEMPLATE"
										>
											{(provided, snapshot) => (
												<MDBox
													ref={provided.innerRef}
													{...provided.droppableProps}
													sx={templatesZoneSx(
														snapshot.isDraggingOver
															? colors.darkBeige
															: colors.beige
													)}
												>
													{category.templateIds.map((templateId, index) => (
														<ConnectedTemplate
															key={templates[templateId]._id}
															template={templates[templateId]}
															index={index}
														/>
													))}
													{provided.placeholder}
												</MDBox>
											)}
										</Droppable>
									</MDBox>
									<Droppable droppableId={"categories"} type="CATEGORY">
										{provided => (
											<MDBox
												ref={provided.innerRef}
												{...provided.droppableProps}
												mt={1.25}
												sx={{
													maxHeight: "calc(100vh - 400px)",
													overflow: "auto"
												}}
											>
												{others.map((category, index) => (
													<ConnectedTemplateCategory
														key={category._id}
														category={category}
														index={index}
													/>
												))}
												{provided.placeholder}
											</MDBox>
										)}
									</Droppable>
								</DragDropContext>
							)}
							{type !== "Template" && (
								<MDTypography
									variant="caption"
									color="text"
									component="p"
									mt={2}
								>
									Icons made by{" "}
									<a
										href="https://www.freepik.com/"
										target="_blank"
										rel="noopener noreferrer"
									>
										Freepik
									</a>{" "}
									from{" "}
									<a
										href="https://www.flaticon.com/"
										target="_blank"
										rel="noopener noreferrer"
									>
										www.flaticon.com
									</a>
								</MDTypography>
							)}
						</MDBox>
						<MDBox p={2} display="flex" justifyContent="flex-end" gap={1}>
							<MDButton
								variant="outlined"
								color="secondary"
								disabled={!areCategoriesReordered}
								onClick={() =>
									fetchGetTemplatesCategories(type, system, community._id)
								}
							>
								{"Restaurer"}
							</MDButton>
							<MDButton
								variant="gradient"
								color="primary"
								disabled={!areCategoriesReordered}
								onClick={() =>
									fetchReorderTemplatesCategories(Object.values(categories))
								}
							>
								{"Réorganiser"}
							</MDButton>
						</MDBox>
					</Card>
				</Grid>
				<Notification
					color={color}
					message={message}
					open={br}
					onClose={this.hideAlert}
				/>
			</Grid>
		);
	}
}

class TemplatesOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Template"
	};
}

class ActivitiesOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Activity"
	};
}

class ProposalsOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Proposal"
	};
}

class SystemTemplatesOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Template",
		system: true
	};
}

class SystemActivitiesOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Activity",
		system: true
	};
}

class SystemProposalsOrderPage extends TemplatesOrder {
	static defaultProps = {
		type: "Proposal",
		system: true
	};
}

const mapStateToProps = state => {
	const {
		isFetching,
		categories,
		areCategoriesReordered,
		snackInfoMsg,
		snackErrorMsg
	} = state.templateCategoryReducer;
	const { templates, community } = state.entitiesReducer;
	const { pictos } = state.uploadReducer;

	return {
		isFetching,
		categories,
		templates,
		areCategoriesReordered,
		snackInfoMsg,
		snackErrorMsg,
		community,
		pictos
	};
};

const mapDispatchToProps = dispatch => {
	let actions = bindActionCreators(
		{
			fetchGetPictosUri,
			fetchGetTemplatesCategories,
			fetchReorderTemplatesCategories
		},
		dispatch
	);
	return { ...actions, dispatch };
};

const ConnectedTemplate = connect(mapStateToProps)(Template);
const ConnectedTemplateCategory = connect(mapStateToProps)(TemplateCategory);

const connectPage = Page => connect(mapStateToProps, mapDispatchToProps)(Page);

export default {
	TemplatesOrderPage: connectPage(TemplatesOrderPage),
	ActivitiesOrderPage: connectPage(ActivitiesOrderPage),
	ProposalsOrderPage: connectPage(ProposalsOrderPage),
	SystemTemplatesOrderPage: connectPage(SystemTemplatesOrderPage),
	SystemActivitiesOrderPage: connectPage(SystemActivitiesOrderPage),
	SystemProposalsOrderPage: connectPage(SystemProposalsOrderPage),
	TemplateCategory: ConnectedTemplateCategory,
	Template: ConnectedTemplate
};
